import React from "react";
import { MdContentCopy } from "react-icons/md";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../../app/hooks";
import { groupAPI, useLazyGetClientCodeByGroupIdQuery } from "../../../../services/groupAPI";
import { LOGIN_CLIENT_CODE_PARAM_KEY } from "../utils";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";

interface UseAtLoginProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  clientCode?: string | null;
  groupId?: string | null;
}

const UseAtLogin: React.FC<UseAtLoginProps> = ({
  checked,
  onChange,
  clientCode,
  groupId,
}) => {

  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const dispatch = useAppDispatch();
  const [getClientCode, { isFetching: isActivating }] =
    useLazyGetClientCodeByGroupIdQuery();

  const loginUrl =
    clientCode &&
    `${window.location.origin}/login?${LOGIN_CLIENT_CODE_PARAM_KEY}=${clientCode}`;

  const handleCopyUrl = () => {
    if (loginUrl) {
      navigator.clipboard.writeText(loginUrl);
      toast.success(language?.groupStyle?.general?.messages?.copiedToClipboard)
    }
  };

  const handleRequestCode = async () => {
    if (!groupId) return;
    try {
      await getClientCode(groupId).unwrap();
      dispatch(groupAPI.util.invalidateTags(["Groups"]));
      toast.success(language?.groupStyle?.general?.messages?.successfulActivate)
    } catch {
      toast.error(language?.groupStyle?.general?.messages?.unsuccessfulActivate)
    }
  };

  const hasNoCode = !clientCode;

  return (
    <div className="flex flex-col w-full max-w-full min-w-0 bg-white rounded-md sm:rounded-lg p-2 sm:p-3 md:p-4 gap-2">
      <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-0">
        <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5">
          {language?.groupStyle?.components?.useAtLogin?.title}
        </label>
        <div className="flex items-center sm:ml-auto flex-shrink-0">
          {hasNoCode ? (
            <button
              type="button"
              onClick={handleRequestCode}
              disabled={!groupId || isActivating}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs sm:text-sm font-medium shadow-md hover:from-blue-700 hover:to-blue-800 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md"
            >
              {isActivating ? language?.groupStyle?.general?.activating : language?.groupStyle?.general?.activate}
            </button>
          ) : (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
              />
              <div className="w-10 h-5 bg-gray-300 rounded-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 peer-checked:bg-blue-600 transition relative">
                <div
                  className={`w-4 h-4 bg-white rounded-full shadow transform transition absolute top-0.5 ${checked ? "translate-x-5" : "translate-x-0.5"
                    }`}
                />
              </div>
            </label>
          )}
        </div>
      </div>
      {!hasNoCode && (
        <button
          type="button"
          onClick={handleCopyUrl}
          className="flex items-center gap-2 mt-1 w-full justify-center sm:justify-start px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm text-gray-700"
        >
          <MdContentCopy className="w-4 h-4 text-gray-600 flex-shrink-0" />
          {language?.groupStyle?.components?.useAtLogin?.copyLoginURL}
        </button>
      )}
    </div>
  );
};

export default UseAtLogin;
